import { AutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/AutoCompleteVirtualize.tsx';
import type { AppInventoryVersionType } from '@openx/types/targeting/appInventory.ts';
import { type AppGroup, AppInventoryType, InventoryContentOption } from '@openx/types/targeting/inventoryAndContent.ts';
import type { ComparisonType } from '@openx/types/targeting/targetingValuesTypes.ts';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../../../utils/context/TargetingContext.tsx';
import { setComparisonOpValue } from '../../../shared/InventoryAndContent/inventoryHelpers.ts';

type AppInventoryInputProps = {
  comparisonType: ComparisonType;
  dispatchInventory: (
    action: ReturnType<(typeof setComparisonOpValue)[typeof InventoryContentOption.APP_GROUP]>
  ) => void;
  currentValues?: string[];
  appInventoryType?: AppInventoryType;
  placeholder?: string;
  testId?: string;
  inventoryVersion?: AppInventoryVersionType;
  isDisabled?: boolean;
  /**
   * Whether the total number of selected items has reached `INVENTORY_LIMITS` for the
   * active App Inventory sub-type. When true, the input is disabled and the placeholder
   * shows the limit reached message.
   */
  isLimitReached?: boolean;
  /**
   * The numeric limit corresponding to the active App Inventory sub-type — only used to
   * render the "limit reached" placeholder. Required together with `isLimitReached`.
   */
  limit?: number;
};

export const AppInventoryInput = ({
  comparisonType,
  dispatchInventory,
  currentValues = [],
  appInventoryType = AppInventoryType.APP_NAME,
  placeholder,
  testId = 'app-inventory-input',
  isDisabled = false,
  isLimitReached = false,
  limit,
}: AppInventoryInputProps) => {
  const { t } = useTranslation();
  const { useAppInventoryFetch } = useTargetingContext();
  const [inputValue, setInputValue] = useState('');

  if (!useAppInventoryFetch) {
    throw new Error('AppInventoryInput requires useAppInventoryFetch in TargetingContext');
  }

  const { data, isLoading } = useAppInventoryFetch({ searchPhrase: inputValue });

  const handleSelect = useCallback(
    (_event: unknown, value: AppGroup | null) => {
      if (!value || isDisabled || isLimitReached) return;

      const selectedValue =
        appInventoryType === AppInventoryType.APP_NAME ? `${value.id}:${value.name}` : `${value.id}`;

      const newValues = [...currentValues, selectedValue];

      dispatchInventory(
        setComparisonOpValue[InventoryContentOption.APP_GROUP]({
          values: newValues,
          comparisonType,
        })
      );

      setInputValue('');
    },
    [appInventoryType, comparisonType, currentValues, dispatchInventory, isDisabled, isLimitReached]
  );

  const handleInputChange = useCallback(
    (_event: unknown, value: string) => {
      if (isDisabled || isLimitReached) return;

      setInputValue(value);
    },
    [isDisabled, isLimitReached]
  );

  const getOptionLabel = useCallback(
    (option: AppGroup) => {
      if (appInventoryType === AppInventoryType.APP_NAME) {
        return option.name;
      }

      return `${option.id} (${option.name})`;
    },
    [appInventoryType]
  );

  const filteredOptions = useMemo(
    () =>
      data.filter(option => {
        const valueToCheck =
          appInventoryType === AppInventoryType.APP_NAME ? `${option.id}:${option.name}` : `${option.id}`;

        return !currentValues.includes(valueToCheck);
      }),
    [data, appInventoryType, currentValues]
  );

  const defaultPlaceholder = useMemo(
    () =>
      appInventoryType === AppInventoryType.APP_NAME
        ? t('Provide or search for App Name')
        : t('Provide or search for App Bundle ID'),
    [appInventoryType, t]
  );

  const limitReachedPlaceholder = useMemo(
    () => (limit !== undefined ? t('The limit has been reached ({limit}).', { limit }) : defaultPlaceholder),
    [limit, t, defaultPlaceholder]
  );

  return (
    <AutoCompleteVirtualize
      options={filteredOptions}
      getOptionLabel={getOptionLabel}
      loading={isLoading}
      onInputChange={handleInputChange}
      onChange={handleSelect}
      inputValue={inputValue}
      value={null}
      textFieldProps={{
        label: appInventoryType === AppInventoryType.APP_NAME ? t('App Name') : t('App Bundle ID'),
        placeholder: isLimitReached ? limitReachedPlaceholder : (placeholder ?? defaultPlaceholder),
        helperText: isLimitReached ? limitReachedPlaceholder : undefined,
      }}
      data-test={testId}
      renderOptions={{
        dataTest: `${testId}-option`,
      }}
      noOptionsText={inputValue.length < 2 ? t('Type at least 2 characters to search') : t('No results found')}
      disabled={isDisabled || isLimitReached}
    />
  );
};
